import { useState, useEffect, useRef, lazy, Suspense, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { logEvent } from '../lib/analytics'
import { coachSeen, markCoachSeen } from '../lib/coachmarks'
import { useNavHeight } from '../hooks/useNavHeight'
import { featureKeyForPath } from '../lib/featureFlags'
import './HubIcons.css'
import { X } from 'lucide-react'

// The real global search, mounted inside the sheet (embedded mode). Lazy so the
// nav's own chunk stays small; it only loads the first time the sheet opens.
const SearchInSheet = lazy(() => import('../pages/Search'))
import { MoonIcon } from './MoonIcon'
import { Wordmark } from './Wordmark'
import { NextEpLogo } from './NextEpLogo'
import { HomeIcon, MyListIcon, ProfileIcon } from './NavIcons'
// 17 (Jason 2026-09-16: the More sheet "reeks of v16 because of the icons"): the hub
// tiles use one thin-stroke set, not the hand-drawn 16 glyphs. The bottom nav keeps
// its own four marks.
import { Compass, Download, Puzzle, ArrowDownUp, Gavel, Radio, Users, MessagesSquare, UserPlus, BarChart3, Film, Bell, Clock, CircleHelp, MessageCircle, PenLine, Settings as SettingsIcon } from 'lucide-react'

const PRIMARY = [
  { to: '/',         icon: HomeIcon,  label: 'Home'    },
  // "Solid Facet" glyph set (2026-08-06) — filled silhouettes, no thin strokes
  // or masked cutouts to lose at the nav's 20px render size. Tonight uses the
  // canonical MoonIcon (also used by WatchPlanButton) rather than a duplicate
  // static glyph — MoonIcon's crescent cutout used to read as a "C" here;
  // its 2026-08-06 redraw fixes that the same way every other icon does here,
  // a full disc with facet shading, never a cutout.
  { to: '/tonight',  icon: MoonIcon,  label: 'Tonight' },  // promoted from the hub (Option A), Discover moved INTO the hub
  null,
  { to: '/list',     icon: MyListIcon,  label: 'My List' },
  { to: '/profile',  icon: ProfileIcon,  label: 'Profile' },
]

const HUB_CATEGORIES = [
  // v17 cut (docs/v17-scope.md, 2026-09-15): the hub is the tracker's utility drawer.
  // Games / People / Aftershow only appear when their toggle is on (Settings › Features).
  // For You, Canon, Shelves, Buzz, Reviews, Challenges and Cards lost their doors here.
  {
    label: 'Go',
    color: 'var(--amber)',
    hex: 'var(--amber)',
    items: [
      { to: '/discover', desc: 'Browse by mood, genre and service', icon: Compass,  label: 'Discover',  anim: 'a-scan' },
      { to: '/import',   desc: 'Bring your list over',   icon: Download,    label: 'Import',    anim: 'a-pulse' },
    ],
  },
  {
    label: 'Play',
    color: 'var(--blue-soft)',
    hex: 'var(--blue-soft)',
    items: [
      { to: '/cliffhanger', desc: 'The daily guessing game', icon: Puzzle, label: 'Cliffhanger', anim: 'a-wobble' },
      { to: '/faceoff',  desc: 'Higher or lower, gut feeling counts', icon: ArrowDownUp, label: 'Face-Off', anim: 'a-pop' },
      { to: '/verdict',  desc: 'One show, five gut calls', icon: Gavel, label: 'Verdict', anim: 'a-wobble' },
    ],
  },
  {
    label: 'Together',
    color: 'var(--state-sync)',
    hex: 'var(--state-sync)',
    items: [
      { to: '/people', desc: 'Find friends, watch together', icon: Users, label: 'People', anim: 'a-bob' },
      // The public wall is server-rendered at this origin (netlify/functions/live.js), so it
      // is a full navigation, not an SPA route. Badge = public activity since you last looked.
      { to: '/live', live: true, desc: 'What everyone is watching right now', icon: Radio, label: 'Live wall', anim: 'a-pulse' },
      { to: '/aftershow', desc: 'Where people are talking', icon: MessagesSquare, label: 'Aftershow', anim: 'a-wave' },
      { key: 'invite', feature: 'people', desc: 'Add a friend', icon: UserPlus, label: 'Invite', invite: true, anim: 'a-pop' },
    ],
  },
  {
    label: 'You',
    color: 'var(--green)',
    hex: 'var(--green)',
    items: [
      { to: '/stats', desc: 'Your viewing story', icon: BarChart3, label: 'Stats', anim: 'a-bars' },
      { key: 'writers', desc: 'Report a bug or pitch an idea', icon: PenLine, label: "Writers' Room", writers: true, anim: 'a-pop' },
      { to: '/notifications', desc: 'Your inbox', icon: Bell, label: 'Notifications', notif: true, anim: 'a-ring' },
      { to: '/history', desc: 'Your full watch log', icon: Clock, label: 'History', anim: 'a-tick' },
      { to: '/wrapped', desc: 'Your year in TV', icon: Film, label: 'Wrapped', anim: 'a-bars' },
      { to: '/help', desc: 'How it works', icon: CircleHelp, label: 'Help', anim: 'a-pulse' },
      // Reachable before this only via Profile → Settings, so global search for
      // "settings" found nothing and fell through to a TMDB title fallback
      // (Jason 2026-09-17, live-testing). Listing it here also surfaces it from the
      // hub sheet, not just search — a reasonable convenience, not just a search fix.
      { to: '/settings', desc: 'Account, notifications, theme', icon: SettingsIcon, label: 'Settings', anim: 'a-pulse' },
      { key: 'discord', desc: 'Chat with the community', icon: MessageCircle, label: 'Discord', external: true, href: 'https://discord.gg/Km87DQHCSm', anim: 'a-pop' },
    ],
  },
]

// THE one place that decides whether an item warrants attention — the row's own
// badge/dot AND the collapsed-section header dot both derive from this, so they
// can never disagree (a dot hidden inside a closed accordion was invisible and
// made the user open every section to find it — Jason 2026-08-15).
function itemAttention(item, { unseenNotifs, newFollowers, cliffUnplayed, faceoffUnplayed, verdictUnplayed, liveUpdates, unseenActivity }) {
  const hasNotif = item.notif && unseenNotifs > 0
  const followerCount = item.to === '/people' ? newFollowers : 0
  const cliffDot = item.to === '/cliffhanger' && cliffUnplayed
  const faceoffDot = item.to === '/faceoff' && faceoffUnplayed
  const verdictDot = item.to === '/verdict' && verdictUnplayed
  const liveCount = item.to === '/live' ? (liveUpdates || 0) : 0
  // Counts, never dots (Jason 2026-10-11: "I like the tiny number indicators"). A game
  // waiting is 1, new friend activity is 1, notices and followers are their real totals.
  const showDot = false
  const badgeCount = hasNotif ? unseenNotifs
    : followerCount > 0 ? followerCount
    : liveCount > 0 ? liveCount
    : (item.notif && unseenActivity) ? 1
    : (cliffDot || faceoffDot || verdictDot) ? 1
    : 0
  return { hasNotif, followerCount, cliffDot, faceoffDot, verdictDot, showDot, badgeCount, any: badgeCount > 0 }
}

// One tile (17): a card the height of two lines, icon top-left in the group colour.
function HubTile({ item, cat, unseenNotifs, newFollowers, cliffUnplayed, faceoffUnplayed, verdictUnplayed, liveUpdates, unseenActivity, onClick }) {
  const { showDot, badgeCount, followerCount } = itemAttention(item, { unseenNotifs, newFollowers, cliffUnplayed, faceoffUnplayed, verdictUnplayed, liveUpdates, unseenActivity })
  const tint = item.off ? 'var(--text-dim)' : (cat?.color || 'var(--amber)')
  return (
    <button onClick={onClick} className="tap-card" style={{ position:'relative', minHeight:88, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'12px 12px 11px', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'flex-start', justifyContent:'space-between', gap:10, fontFamily:'var(--font)', textAlign:'left', opacity: item.off ? 0.8 : 1 }}>
      <span style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%' }}>
        <span style={{ width:30, height:30, borderRadius:9, background:`color-mix(in srgb, ${tint} 14%, transparent)`, display:'flex', alignItems:'center', justifyContent:'center', color:tint }}><item.icon size={18} color="currentColor" strokeWidth={1.7} /></span>
        {item.off && <span style={{ fontFamily:'var(--font-mono)', fontSize:9.5, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--amber)', border:'1px solid rgba(var(--amber-rgb),0.35)', borderRadius:'var(--radius-pill)', padding:'2px 7px' }}>Off</span>}
        {!item.off && badgeCount > 0 && <span data-dynamic style={{ minWidth:18, height:18, borderRadius:'var(--radius-pill)', background:'var(--state-bad)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'var(--text-strong)', padding:'0 5px' }}>{badgeCount > 9 ? '9+' : badgeCount}</span>}
        {!item.off && badgeCount === 0 && showDot && <span data-dynamic style={{ width:8, height:8, borderRadius:'50%', background:'var(--state-bad)' }} />}
      </span>
      <span style={{ minWidth:0, width:'100%' }}>
        <span style={{ display:'block', color: item.off ? 'var(--text-muted)' : 'var(--text)', fontSize:14.5, fontWeight:600, lineHeight:1.2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{item.label}</span>
        <span style={{ color:'var(--text-dim)', fontSize:11.5, lineHeight:1.3, marginTop:3, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>{followerCount > 0 ? `${followerCount} new ${followerCount === 1 ? 'follower' : 'followers'}` : item.desc}</span>
      </span>
    </button>
  )
}

// One row: icon tile + title + description, meaning-tinted to its category. Shared
// by the category accordions, search results, and the "Needs attention" strip so a
// row looks the same wherever it appears.
function HubRow({ item, cat, unseenNotifs, newFollowers, cliffUnplayed, faceoffUnplayed, verdictUnplayed, liveUpdates, unseenActivity, onClick }) {
  // v17: the same row the show page's More sheet uses — 32px icon tile, label, one
  // dim line. No gradient tiles, no idle animations, no chevron.
  const { showDot, badgeCount, followerCount } = itemAttention(item, { unseenNotifs, newFollowers, cliffUnplayed, faceoffUnplayed, verdictUnplayed, liveUpdates, unseenActivity })
  return (
    <button onClick={onClick} className="tap-card" style={{ width:'100%', minHeight:52, background:'transparent', border:'none', padding:'6px 4px', cursor:'pointer', display:'flex', alignItems:'center', gap:14, fontFamily:'var(--font)', textAlign:'left' }}>
      <span style={{ width:32, height:32, borderRadius:10, background:'var(--surface)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, color: item.off ? 'var(--text-dim)' : (cat?.color || 'var(--amber)') }}>
        <item.icon size={18} color="currentColor" strokeWidth={1.9} />
      </span>
      <span style={{ flex:1, minWidth:0 }}>
        <span style={{ display:'block', color: item.off ? 'var(--text-muted)' : 'var(--text)', fontSize:15, lineHeight:1.2 }}>{item.label}</span>
        {(followerCount > 0 || item.desc) && (
          <span style={{ display:'block', color:'var(--text-dim)', fontSize:12.5, lineHeight:1.3, marginTop:2 }}>
            {followerCount > 0 ? `${followerCount} new ${followerCount === 1 ? 'follower' : 'followers'}` : item.desc}
          </span>
        )}
      </span>
      {badgeCount > 0 && (
        <span data-dynamic style={{ minWidth:18, height:18, borderRadius:'var(--radius-pill)', background:'var(--state-bad)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'var(--text-strong)', padding:'0 5px', flexShrink:0 }}>{badgeCount > 9 ? '9+' : badgeCount}</span>
      )}
      {item.off && <span style={{ fontFamily:'var(--font-mono)', fontSize:10, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--amber)', border:'1px solid rgba(var(--amber-rgb),0.35)', borderRadius:'var(--radius-pill)', padding:'3px 8px', flexShrink:0 }}>Off</span>}
      {badgeCount === 0 && showDot && <span data-dynamic style={{ width:8, height:8, borderRadius:'50%', background:'var(--state-bad)', flexShrink:0 }} />}
    </button>
  )
}

// Every hub door, flattened — the global search page's "Go to" section (pages/Search.jsx).
export const HUB_DESTINATIONS = HUB_CATEGORIES.flatMap(c => c.items)

export function BottomNav({ tonightCount = 0, unseenActivity = false, unseenNotifs = 0, onClearNotifs, newFollowers = 0, cliffUnplayed = false, faceoffUnplayed = false, verdictUnplayed = false, liveUpdates = 0, onOpenLive, featureFlags, onInvite, wl, user }) {
  // Basic/Advanced/Custom app mode (lib/featureFlags.js): drop any hub item whose
  // feature is off, then drop any category that's now empty. `isEnabled` treats an
  // item with no mapped feature key as always-on (core surfaces), so this is a
  // no-op for anyone in Advanced mode (the default — no existing user's nav changes).
  const isEnabled = featureFlags?.isEnabled || (() => true)
  // Basic (the default for new accounts) used to HIDE games, People, the Aftershow and
  // Wrapped from this sheet, leaving one "Unlock more" row below the fold that landed on
  // the Settings hub. Nobody found them (Jason 2026-09-16: "do new users know where to
  // look?"). Now every door stays in the sheet; an off feature reads "Off · tap to turn
  // on" and opens its own gate page, which has the one-tap switch. Invite (an action, not
  // a page) still needs People on.
  const visibleCategories = HUB_CATEGORIES
    .map(cat => ({ ...cat, items: cat.items
      .filter(i => !i.invite || isEnabled('people'))
      .map(i => { const k = i.feature || featureKeyForPath(i.to || ''); return (k && !isEnabled(k)) ? { ...i, off: true, desc: 'Off · tap to turn on' } : i })
    }))
    .filter(cat => cat.items.length > 0)
  const [open, setOpen] = useState(false)
  const navTotal = (unseenNotifs || 0) + (newFollowers || 0) + (cliffUnplayed ? 1 : 0) + (faceoffUnplayed ? 1 : 0) + (verdictUnplayed && isEnabled('verdict') ? 1 : 0) + (liveUpdates || 0) + (unseenActivity ? 1 : 0)
  // Live search inside the sheet (2026-10-10). `searching` hides the directory while
  // results are up; remounting SearchInSheet on each open resets the field.
  const [searching, setSearching] = useState(false)
  const onSearchQuery = useCallback((q) => setSearching((q || '').trim().length >= 2), [])
  const leaveSheet = useCallback(() => setOpen(false), [])
  // Evening cue for Tonight (2026-09-12): after 5pm local with nothing planned, the
  // moon lights amber — a time-of-day nudge toward the app's most evening-skewed page.
  // With plans it already carries the count badge instead. Minute ticker so a tab
  // left open crosses 5pm honestly.
  const [navTick, setNavTick] = useState(() => Date.now())
  useEffect(() => { const t = setInterval(() => setNavTick(Date.now()), 60000); return () => clearInterval(t) }, [])
  // After a one-tap standalone update (WhatsNew oneTapUpdate → ?_nav=1): WebKit can
  // leave this fixed nav drifted mid-screen after an in-place navigation. Re-anchor it
  // on the first frames: drop to static, force layout, back to fixed, scroll to top,
  // then strip the marker so a later reload doesn't repeat it.
  useEffect(() => {
    let u; try { u = new URL(window.location.href) } catch { return }
    if (u.searchParams.get('_nav') !== '1') return
    u.searchParams.delete('_nav'); u.searchParams.delete('_v')
    try { window.history.replaceState(window.history.state, '', u.toString()) } catch {}
    // 17.099: the position static→'' toggle that used to live here was the drift bug
    // itself (see the effect below). Only the scroll-to-top remains.
    const r1 = requestAnimationFrame(() => { window.scrollTo(0, 0) })
    return () => cancelAnimationFrame(r1)
  }, [])
  const navLoc = useLocation()
  // Self-healing anchor (Jason 2026-10-12: "you have to scroll for eternity to see the
  // bottom menu" on Notifications, My List and other long pages). The _nav=1 re-anchor
  // above only covers the one-tap update; WebKit can drift a fixed bar after other
  // in-place navigations too. So: measure where the bar actually is on scroll, resize,
  // tab return and route change, and if it isn't sitting on the bottom edge, snap it
  // back the same way. Skipped while a field has focus (the keyboard legitimately moves
  // the viewport). Each correction is logged so the field frequency is known.
  useEffect(() => {
    const el = navRef.current; if (!el) return
    let armed = true
    const vhNow = () => (window.visualViewport && window.visualViewport.height) || window.innerHeight
    const drifted = () => { const r = el.getBoundingClientRect(); return r.height > 0 && Math.abs(r.bottom - vhNow()) > 2 }
    // Diagnostics (2026-10-13): Jason's bar still lands at the document bottom on iOS
    // standalone on 17.096, after the Wrap fix, and neither Chromium nor desktop WebKit
    // reproduce it. For a fixed element, offsetParent is null unless an ancestor is a
    // containing block (transform/filter/contain) — so it names the culprit directly.
    const diag = () => {
      const cs = (n) => { try { const c = window.getComputedStyle(n); return [c.transform, c.filter, c.animationName, c.overflow, c.contain, c.position].join('|') } catch { return '?' } }
      let standalone = false; try { standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true } catch {}
      return { op: el.offsetParent ? (el.offsetParent.tagName + (el.offsetParent.id ? '#' + el.offsetParent.id : '')) : null, html: cs(document.documentElement), body: cs(document.body), nav: cs(el), sh: document.documentElement.scrollHeight, sy: Math.round(window.scrollY), standalone, vv: window.visualViewport ? Math.round(window.visualViewport.offsetTop) : null }
    }
    // 17.099 (2026-10-13): THE DRIFT WAS THIS EFFECT. On iOS the visual viewport sits a
    // few px off the layout viewport (URL bar, safe area, keyboard settling), so the
    // check fired on an 11px false positive while the bar was still fixed; the
    // "re-anchor" then set el.style.position='static' and cleared it — and React never
    // re-applies an inline style whose prop hasn't changed, so the bar stayed STATIC,
    // i.e. a normal element at the very end of <body>: "pushed all the way to the
    // bottom of the page". Diagnostics from the simulator guest (op:BODY, nav
    // position:static, bottom = scrollHeight - scrollY) proved it. The bar is never
    // touched from here again; this effect only measures, with a tolerance that
    // ignores a visual-viewport offset.
    const anchor = () => 'observe-only'
    const check = () => {
      if (!armed) return
      const a = document.activeElement
      if (a && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName)) return
      const vh = vhNow()
      const r = el.getBoundingClientRect()
      if (r.height === 0) return
      let vvOff = 0; try { vvOff = window.visualViewport ? Math.abs(window.visualViewport.offsetTop) : 0 } catch {}
      if (Math.abs(r.bottom - vh) > Math.max(24, vvOff + 8)) {
        armed = false
        const d = diag()
        const fixed = anchor()
        try { logEvent('nav_drift', { path: window.location.pathname, bottom: Math.round(r.bottom), vh: Math.round(vh), fixed, ...d }) } catch {}
        setTimeout(() => { armed = true }, 1500)
      }
    }
    let raf = 0
    const onScroll = () => { if (raf) return; raf = requestAnimationFrame(() => { raf = 0; check() }) }
    const onShow = () => setTimeout(check, 120)
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onShow)
    window.addEventListener('orientationchange', onShow)
    window.addEventListener('pageshow', onShow)
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') onShow() })
    window.visualViewport?.addEventListener('resize', onShow)
    const t = setTimeout(check, 400)
    return () => {
      clearTimeout(t); if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onShow)
      window.removeEventListener('orientationchange', onShow); window.removeEventListener('pageshow', onShow)
      window.visualViewport?.removeEventListener('resize', onShow)
    }
  }, [navLoc.pathname])
  const navHour = new Date(navTick).getHours()
  const tonightLit = (navHour >= 17 || navHour < 2) && tonightCount === 0
  // Collapsible categories (design sync 2026-08-15): the hub had grown to ~14 items
  // across 5 always-expanded sections, all visible at once whether you wanted them
  // or not. Discover opens by default (the section a first-timer lands on); the rest
  // start closed so the sheet reads as a short list of headings, not a long scroll.
  // Publish the nav's real height as --nav-h; nothing should hardcode it.
  const navRef = useRef(null)
  useNavHeight(navRef)
  // One-time pointer at the center "More" hub — a first-timer has no signal that
  // ~14 features (Discover, Challenges, Stats, Cards, Import, Help…) live behind it.
  const [moreCoach, setMoreCoach] = useState(false)
  useEffect(() => {
    if (coachSeen('more-hub')) return
    const t = setTimeout(() => setMoreCoach(true), 900)  // let the page settle first
    return () => clearTimeout(t)
  }, [])
  const dismissMoreCoach = () => { markCoachSeen('more-hub'); setMoreCoach(false) }
  // The bubble sits exactly where the mark/Undo rail lands, so the first mark from
  // the deck put "Everything else is in here" on top of "Marked S1·E1 · Undo" (seen
  // in a headless gesture run, 2026-09-15). A mark means the main action is found;
  // the coach steps aside for good rather than fighting the rail.
  useEffect(() => {
    if (!moreCoach) return
    const off = () => dismissMoreCoach()
    window.addEventListener('nextep:episode-watched', off)
    return () => window.removeEventListener('nextep:episode-watched', off)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moreCoach])
  const navigate  = useNavigate()
  const location  = useLocation()
  const activeHub = HUB_CATEGORIES.flatMap(c => c.items).find(i => i.to === location.pathname)

  useEffect(() => { setOpen(false) }, [location.pathname])

  // Needs attention: only real, live badge state — never a static "check this out"
  // entry. Empty when there's genuinely nothing to flag.
  const needsAttention = []
  if (unseenNotifs > 0) {
    const notifItem = HUB_CATEGORIES.flatMap(c => c.items).find(i => i.notif)
    needsAttention.push({ ...notifItem, badgeCount: unseenNotifs })
  }
  if (cliffUnplayed && isEnabled('cliffhanger')) {
    const cliffItem = HUB_CATEGORIES.flatMap(c => c.items).find(i => i.to === '/cliffhanger')
    needsAttention.push({ ...cliffItem, dot: true })
  }
  if (faceoffUnplayed && isEnabled('faceoff')) {
    const faceoffItem = HUB_CATEGORIES.flatMap(c => c.items).find(i => i.to === '/faceoff')
    needsAttention.push({ ...faceoffItem, dot: true })
  }
  if (verdictUnplayed && isEnabled('verdict')) {
    const verdictItem = HUB_CATEGORIES.flatMap(c => c.items).find(i => i.to === '/verdict')
    needsAttention.push({ ...verdictItem, dot: true })
  }
  if (liveUpdates > 0) {
    const liveItem = HUB_CATEGORIES.flatMap(c => c.items).find(i => i.to === '/live')
    needsAttention.push({ ...liveItem, badgeCount: liveUpdates })
  }


  const handleItem = (item) => {
    setOpen(false)
    if (item.invite) { onInvite?.(); return }
    if (item.live) { onOpenLive?.(); window.location.assign(item.to + '?ref=more'); return }
    if (item.writers) { window.dispatchEvent(new CustomEvent('nextep:open-writers-room', { detail: { source: 'nav_hub' } })); return }
    if (item.external) { window.open(item.href, '_blank', 'noopener'); return }
    if (item.notif) onClearNotifs?.()
    navigate(item.to)
  }

  return createPortal((
    <>
      <style>{`
        @keyframes navBadgePop {
          0% { transform: scale(0.4); opacity: 0; }
          60% { transform: scale(1.12); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      {/* Backdrop */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position:'fixed', inset:0, zIndex:998,
            background:'rgba(var(--bg-rgb),0.75)',
            backdropFilter:'blur(6px)',
            WebkitBackdropFilter:'blur(6px)',
          }}
        />
      )}

      {/* Backing for the island while the sheet is open: the sheet scrolls, and its rows
          passed visibly under the floating bar. Same material, faded in at the top. */}
      {open && <div aria-hidden="true" style={{ position:'fixed', left:0, right:0, bottom:0, height:'calc(var(--nav-h) + 14px)', zIndex:'var(--z-menu)', background:'var(--sheet)', WebkitMaskImage:'linear-gradient(to top, #000 68%, transparent)', maskImage:'linear-gradient(to top, #000 68%, transparent)', pointerEvents:'none' }} />}

      {/* Hub sheet */}
      {/* data-visual-offscreen when closed: this is parked at bottom:-100%, invisible to
          users — but a fullPage screenshot captures the ENTIRE scroll height, so the
          parked sheet got photographed mid-page and covered the real content underneath.
          The visual baselines masked it as an artifact, not as UI (e2e/visual.spec.js). */}
      <div {...(open ? {} : { 'data-visual-offscreen': '' })} inert={!open} aria-hidden={!open} style={{
        position:'fixed',
        bottom: open ? 0 : '-100%',
        left:0, right:0,
        zIndex:999,
        background:'var(--sheet)',
        borderTop:'1px solid var(--border)',
        borderRadius:'22px 22px 0 0',
        padding:'16px 20px calc(150px + env(safe-area-inset-bottom)) 20px',
        transition:'bottom 0.32s var(--ease-soft)',
        fontFamily:'var(--font)',
        maxHeight:'80vh',
        overflowY:'auto',
        scrollbarWidth:'none',
      }}>
        {/* Drag handle */}
        <div style={{ width:36, height:4, borderRadius:2, background:'var(--surface-strong)', margin:'0 auto 18px' }}/>

        {/* Centered column so desktop doesn't stretch tiles full-bleed */}
        <div style={{ maxWidth:640, margin:'0 auto' }}>

        {/* Header row, "The Index" masthead (v2.5 MORE-A): gold eyebrow over a serif
            title, the same editorial voice as Home and the Final Cut. */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:18 }}>
          <div>
            <span style={{ display:'inline-flex', alignItems:'center', gap:8 }}><NextEpLogo size={16} glow={false} /><Wordmark size={15} /></span>
            <div style={{ fontFamily:'var(--font-serif)', color:'var(--text-strong)', fontSize:28, fontWeight:500, letterSpacing:'-0.01em', lineHeight:1.1, marginTop:6 }}>
              More
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="tap-card ui-hit"
            style={{ background:'var(--surface)', border:'none', borderRadius:'var(--radius-full)', width:28, height:28, cursor:'pointer', color:'var(--text-dim)', display:'flex', alignItems:'center', justifyContent:'center' }}
          >
            <X size={14}/>
          </button>
        </div>

        {/* Search: ONE tap to the real global search (pages/Search.jsx) — this used to be
            a live input that filtered menu labels and then handed the text to a SECOND
            input on Discover ("you type into the input field and it takes you to yet
            another input field", Jason 2026-09-14). Page names are matched there too. */}
        {/* 2026-10-10: and now the input IS the search. Type here, results rise in
            below, the directory steps aside; tapping a result closes the sheet and
            goes straight there. No second page, no second input. */}
        {open && (
          <Suspense fallback={<div style={{ height:42, marginBottom:18, borderRadius:'var(--radius-md)', background:'var(--surface)', border:'1px solid var(--border)' }} />}>
            <SearchInSheet embedded wl={wl} user={user} featureFlags={featureFlags} onLeave={leaveSheet} onQueryChange={onSearchQuery} />
          </Suspense>
        )}

        {!searching && (
          <>
          {/* Needs attention: real badge state only, surfaced above the categories so
              the one or two things actually worth a tap don't get lost in a 14-item
              directory (design sync 2026-08-15). Nothing renders when there's nothing
              live to flag. */}
          {needsAttention.length > 0 && (
            <div style={{ marginBottom:20 }}>
              <div style={{ color:'var(--text-dim)', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.14em', marginBottom:6 }}>
                Needs attention
              </div>
              {needsAttention.map(item => {
                const cat = HUB_CATEGORIES.find(c => c.items.some(i => (i.to || i.key) === (item.to || item.key)))
                return (
                  <HubRow key={item.to || item.key} item={item} cat={cat}                     unseenNotifs={unseenNotifs} newFollowers={newFollowers} cliffUnplayed={cliffUnplayed} faceoffUnplayed={faceoffUnplayed} verdictUnplayed={verdictUnplayed} liveUpdates={liveUpdates} unseenActivity={unseenActivity}
                    onClick={() => handleItem(item)} />
                )
              })}
            </div>
          )}

          {/* The Index (v2.5 MORE-A): a quiet editorial directory, the contents page of a
              fine magazine — kept as full rows (icon tile + title + description), not a
              colored glass-tile grid; that tile-grid look was deliberately retired once
              already (2026-07-22-ish) for reading as noisier and less scannable than a
              row list. Categories are now collapsible accordions (design sync 2026-08-15)
              so a returning user sees five headings, not fourteen rows, until they open
              one. No `list-stagger`/remount key on the rows themselves — the SHEET's
              slide-up is the single entrance; a second cascade past the slide's 320ms
              was the "stagger" Jason flagged 2026-07-22. */}
          {visibleCategories.map(cat => {
            const isOpen = true   // v17: every group stays open; four short groups need no accordion
            return (
              <div key={cat.label} style={{ marginBottom:10 }}>
                <div
                  style={{
                    width:'100%', background:'transparent', border:'none', borderRadius:'var(--radius)',
                    padding:'6px 4px', display:'flex', alignItems:'center', gap:10,
                    fontFamily:'var(--font)', textAlign:'left',
                  }}
                >
                  <span style={{ color:'var(--amber)', fontFamily:'var(--font-mono)', fontSize:10.5, letterSpacing:'0.12em', textTransform:'uppercase', whiteSpace:'nowrap' }}>{cat.label}</span>
                  <span style={{ flex:1 }}/>
                  {/* Bubble the attention dot up to the header while the section is
                      CLOSED — a dot inside a collapsed accordion is invisible and
                      forced a section-by-section hunt (Jason 2026-08-15). Same
                      itemAttention rule the rows use, so they can't disagree; the
                      header dot yields to the row dots the moment the section opens. */}
                  {!isOpen && (() => {
                    const n = cat.items.reduce((acc, i) => acc + itemAttention(i, { unseenNotifs, newFollowers, cliffUnplayed, faceoffUnplayed, verdictUnplayed, liveUpdates, unseenActivity }).badgeCount, 0)
                    return n > 0 ? (
                      <span data-dynamic style={{
                        minWidth:18, height:18, borderRadius:'var(--radius-pill)', background:'var(--state-bad)', flexShrink:0,
                        display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'var(--text-strong)', padding:'0 5px',
                        '--nxa':'navBadgePop 0.3s cubic-bezier(0.34,1.56,0.64,1)',
                      }}>{n > 9 ? '9+' : n}</span>
                    ) : null
                  })()}
                </div>
                {/* 17 facelift (Jason 2026-09-16: "identical to 16"): tiles in two columns,
                    not a row list. Icon in the group's colour, label, one dim line. */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, paddingTop:4 }}>
                  {cat.items.map(item => <HubTile key={item.to || item.key} item={item} cat={cat} unseenNotifs={unseenNotifs} newFollowers={newFollowers} cliffUnplayed={cliffUnplayed} faceoffUnplayed={faceoffUnplayed} verdictUnplayed={verdictUnplayed} liveUpdates={liveUpdates} unseenActivity={unseenActivity} onClick={() => handleItem(item)} />)}
                </div>
              </div>
            )
          })}
          </>
        )}
        </div>{/* /centered column */}
      </div>

      {/* Nav bar */}
      {/* The island (2026-10-12, Jason: "the main bar feels disjointed from the app"):
          the bar is a detached pill on the SAME material as the More sheet — --sheet
          surface, the sheet's border and shadow, full radius — lifted off the home
          indicator, so bar and sheet read as one object in two states. The outer <nav>
          is a transparent fixed strip that only carries the safe-area padding; it does
          not take taps, so content in the gutters stays reachable. */}
      <nav ref={navRef} aria-label="Main" className="app-bottom-nav" style={{
        position:'fixed', bottom:0, left:0, right:0,
        background:'transparent', pointerEvents:'none',
        display:'flex', alignItems:'stretch',
        padding:'0 16px max(18px, calc(var(--chrome-bottom) + 6px))',   // see --chrome-bottom in index.css
        zIndex: 'var(--z-menu)',
        // NO 3D transform here: on iOS standalone PWAs a position:fixed element that ALSO
        // carries a translateZ/backface hint gets promoted to its own compositor layer and
        // re-anchored against the layout viewport on a repaint — so it DRIFTS to mid-screen
        // when other overlays (co-watch ViewingRoom / update sheet / toast) churn the
        // compositor. That self-transform was the "floating nav" bug, not the fix for it
        // (Jason 2026-07-22). Plain position:fixed stays pinned.
      }}>
        {/* One-time "More hub" pointer, anchored above the center button */}
        {/* HOME ONLY. This bubble is anchored 12px above the nav, which is precisely
            where a page's sticky primary CTA sits, and the nav's z-index means the tip
            always paints over it — so on the Aftershow it covered "Say something", and it
            would do the same on ShowDetail and CuratorLists. 15.794 stopped it EATING
            those taps (pointerEvents:'none'); this stops it HIDING them.
            Nudging the offset upward just trades that for an arrow pointing at nothing on
            the pages with no CTA. Home is where a first-timer lands and has no sticky CTA,
            so the tip belongs there and the collision class disappears. */}
        <div className="nx-island" style={{
          flex:1, display:'flex', alignItems:'stretch', pointerEvents:'auto',
          // Its own surface, a clear step above the page on an OLED ground (Jason 2026-10-13:
          // the first island "looks the same"): text colour mixed into the page colour,
          // so it lifts on every theme, plus a 1px top highlight and a deep, wide shadow.
          height:62, background:'linear-gradient(180deg, color-mix(in srgb, var(--text-strong) 13%, var(--bg)), color-mix(in srgb, var(--text-strong) 8%, var(--bg)))',
          border:'1px solid color-mix(in srgb, var(--text-strong) 18%, transparent)',
          borderRadius:'var(--radius-pill)', boxShadow:'0 18px 42px rgba(0,0,0,0.42), 0 2px 8px rgba(0,0,0,0.26), inset 0 1px 0 rgba(var(--ink-rgb),0.12)',
          position:'relative',
        }}>
        {moreCoach && !open && location.pathname === '/' && (
          // pointerEvents:'none' — a HINT MUST NEVER EAT A TAP. This bubble is anchored
          // above the nav, which is exactly where a page's sticky primary CTA sits, and
          // it was intercepting clicks on it: "Say something" in the Aftershow did
          // nothing when tapped near its centre and worked at its edges, because the
          // bubble covers the middle ~288px (found via elementFromPoint, 2026-07-29 —
          // this is the long-standing "FAB overlap" note). It affects every surface with
          // a bottom CTA, not just this one. The dismiss button re-enables pointer
          // events for itself so the X still works.
          <div data-dynamic style={{ position:'absolute', bottom:'calc(100% + 12px)', left:'50%', transform:'translateX(-50%)', width:'min(288px, 84vw)', zIndex:1, '--nxa':'fadeIn 0.35s ease', pointerEvents:'none' }}>
            <div style={{ position:'relative', background:'var(--sheet)', border:'1px solid rgba(var(--amber-rgb),0.4)', borderRadius:'var(--radius-md)', padding:'11px 12px', boxShadow:'0 10px 30px rgba(0,0,0,0.5)', display:'flex', gap:9, alignItems:'flex-start' }}>
              <span aria-hidden="true" style={{ fontSize:15, lineHeight:1.3 }}>✨</span>
              <span style={{ flex:1, color:'var(--text)', fontSize:12.5, lineHeight:1.45 }}>
                <b style={{ color:'var(--amber)' }}>Everything else is in here.</b> Tap <b>More</b> for Discover, Import, Stats, History and Help.
              </span>
              <button className="ui-hit" onClick={dismissMoreCoach} aria-label="Dismiss tip" style={{ flexShrink:0, background:'none', border:'none', color:'var(--text-dim)', cursor:'pointer', padding:2, display:'flex', pointerEvents:'auto' }}>
                <X size={14}/>
              </button>
              {/* downward pointer */}
              <div style={{ position:'absolute', bottom:-6, left:'50%', transform:'translateX(-50%) rotate(45deg)', width:12, height:12, background:'var(--sheet)', borderRight:'1px solid rgba(var(--amber-rgb),0.4)', borderBottom:'1px solid rgba(var(--amber-rgb),0.4)' }}/>
            </div>
          </div>
        )}
        {PRIMARY.map((item, i) => {
          // Center slot is always the "More" hub — for everyone. (It previously
          // swapped to a Discover shortcut until 3 shows, but that trapped new
          // users: Notifications/People/Import/Help live ONLY in the hub, and the
          // unread badge only renders on the hub variant — so a brand-new user
          // couldn't reach them or even see they had alerts. The hub already leads
          // with Discover as its first item, so Browse stays one tap in.) audit 2026-07-08
          if (!item) return (
            <button key="hub" aria-expanded={open} onClick={() => { if (moreCoach) dismissMoreCoach(); setOpen(v => !v) }} style={{
              flex:1, display:'flex', flexDirection:'column', alignItems:'center',
              justifyContent:'center', gap:2, background:'none', border:'none',
              cursor:'pointer', padding:'0 0 3px', height:'62px', boxSizing:'border-box',
            }}>
              {/* The raised disc: breaks the pill's top edge so the bar is unmistakably one
                  object with a centre, and the sheet visibly grows out of this point. */}
              <div style={{ position:'relative', marginTop:-22 }}>
                <div style={{
                  width:52, height:52, borderRadius:'var(--radius-full)',
                  background: (open || activeHub) ? 'var(--amber)' : 'linear-gradient(180deg, color-mix(in srgb, var(--amber) 30%, var(--bg)), color-mix(in srgb, var(--amber) 16%, var(--bg)))',
                  border:'1.5px solid rgba(var(--amber-rgb),' + ((open || activeHub) ? '1' : '0.6') + ')',
                  boxShadow:'0 10px 24px rgba(0,0,0,0.5), 0 0 0 4px var(--bg)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  transition:'all 0.2s',
                  transform: open ? 'rotate(45deg)' : 'none',
                }}>
                  {open
                    ? <X size={21} color="var(--amber-ink)" strokeWidth={2.5}/>
                    : <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <rect x="1" y="4" width="22" height="16" rx="2" stroke={activeHub ? 'var(--amber-ink)' : 'var(--amber)'} strokeWidth="1.6"/>
                        <polygon points="10,8 10,16 17,12" fill={activeHub ? 'var(--amber-ink)' : 'var(--amber)'}/>
                      </svg>
                  }
                </div>
                {/* Surface unseen Notifications on the primary bar: a count for actionable
                    notifs (recs/challenges), or a plain dot when it's just new friend
                    activity (which lives in Notifications, not Profile, the dot now points
                    at its actual payoff). */}
                {/* One total on the primary bar: notices + new followers + a waiting game
                    each + new friend activity. Never a bare dot (2026-10-11). */}
                {!open && navTotal > 0 && (
                  <div aria-label={`${navTotal} waiting`} style={{
                    position:'absolute', top:-1, right:-1,
                    minWidth:16, height:16, borderRadius:'var(--radius-xs)',
                    background:'var(--state-bad)', border:'1.5px solid var(--bg)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:9, fontWeight:700, color:'var(--text-strong)', padding:'0 2px',
                  }}>
                    {navTotal > 9 ? '9+' : navTotal}
                  </div>
                )}
              </div>
              <span style={{ color:'var(--amber)', fontSize:'9px', fontWeight:600 }}>
                {open ? 'Close' : (activeHub ? activeHub.label : 'More')}
              </span>
            </button>
          )
          return (
            <NavLink key={item.to} to={item.to} end={item.to==='/'}
              // Tapping the tab you're already on scrolls that page to the top — the
              // single most common dead-tap in friction telemetry (2026-09-21: 7 devices
              // tapping Home while on Home, expecting exactly this).
              onClick={() => { const p = window.location.pathname; const here = item.to === '/' ? p === '/' : p.startsWith(item.to); if (here) window.scrollTo({ top: 0, behavior: 'smooth' }) }}
              style={({ isActive }) => ({
              display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
              gap:3, textDecoration:'none', fontSize:'10px', fontWeight:isActive?700:500,
              color: isActive ? 'var(--amber)' : 'var(--text-dim)',
              flex:1, padding:'6px 0 5px', transition:'color 0.15s', height:'60px', boxSizing:'border-box',
              position:'relative',
            })}>
              {({ isActive }) => (<>
              <div className={isActive ? 'nav-glyph-on' : undefined} style={{ position:'relative', display:'inline-flex', alignItems:'center', justifyContent:'center', width:40, height:26, borderRadius:'var(--radius-pill)', background: isActive ? 'rgba(var(--amber-rgb),0.16)' : 'transparent', transition:'background 0.18s' }}>
                {item.to === '/tonight' && tonightLit
                  ? <span style={{ display:'inline-flex', color:'var(--amber)', filter:'drop-shadow(0 0 5px rgba(var(--amber-rgb),0.7))', '--nxa':'nxMoonBreath 3.2s ease-in-out infinite' }} aria-label="It's evening — pick tonight's episode"><item.icon size={20} strokeWidth={1.8}/></span>
                  : <item.icon size={20} strokeWidth={1.8}/>}
                {/* Tonight answers its question before you tap it: how many episodes are
                    planned for tonight. Count, not dot — the number IS the information. */}
                {item.to === '/tonight' && tonightCount > 0 && (
                  <span aria-label={`${tonightCount} planned tonight`} style={{
                    position:'absolute', top:-6, right:-10, minWidth:16, height:16, padding:'0 4px', borderRadius:8,
                    background:'var(--amber)', color:'var(--amber-ink)', fontSize:9.5, fontWeight:600,
                    display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1, boxSizing:'border-box',
                  }}>{tonightCount > 9 ? '9+' : tonightCount}</span>
                )}
              </div>
              {item.label}
              </>)}
            </NavLink>
          )
        })}
        </div>
      </nav>
    </>
  ), document.body)
}
